import { useEffect, useRef, useState } from 'react';

/**
 * Shows a small banner at the top of the page whenever the participant's
 * WebSocket connection is down or reconnecting. Also briefly flashes
 * "Reconnected" when it comes back, then auto-hides.
 *
 * Purpose: makes silent mid-session disconnects visible so users don't
 * prematurely refresh the page (which kicks them out of the session).
 */
const ReconnectBanner = ({ isConnected, reconnecting }) => {
  const [showReconnected, setShowReconnected] = useState(false);
  const wasConnectedRef = useRef(isConnected);

  useEffect(() => {
    // Flash "Reconnected" briefly when transitioning from disconnected → connected
    if (!wasConnectedRef.current && isConnected) {
      setShowReconnected(true);
      const t = setTimeout(() => setShowReconnected(false), 2000);
      wasConnectedRef.current = isConnected;
      return () => clearTimeout(t);
    }
    wasConnectedRef.current = isConnected;
    return undefined;
  }, [isConnected]);

  const isOffline = !isConnected || reconnecting;

  if (!isOffline && !showReconnected) return null;

  const baseStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    padding: '10px 16px',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: 500,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    transition: 'background-color 0.2s ease',
  };

  if (isOffline) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{ ...baseStyle, backgroundColor: '#f59e0b' }}
      >
        <span
          style={{
            display: 'inline-block',
            width: '14px',
            height: '14px',
            border: '2px solid #fff',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'reconnect-spin 0.9s linear infinite',
          }}
        />
        <span>Reconnecting… your session is still active. Please don't refresh.</span>
        <style>{`
          @keyframes reconnect-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{ ...baseStyle, backgroundColor: '#10b981' }}
    >
      <span>✅ Reconnected</span>
    </div>
  );
};

export default ReconnectBanner;
